import React, { useEffect, useState } from 'react';
import { useFilters } from '../../../context/FilterContext';
import {
    Box,
    Button,
    Grid,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    CircularProgress,
    Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import { toast } from 'react-toastify';
import { getAllCOE, getAllEnrolmentYears, getAllSemesters } from "../../../services/adminServices/CoeServices";
import LabPracticeModal from './LabPracticeModal';
import LabPracticeTable from './LabPracticeTable';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import { getLabType } from '../../../services/mentorServices/MentorServices';

const LabComponent = () => {
    const { labComponentState, setLabComponentState } = useFilters();

    const [openModal, setOpenModal] = useState({ userForm: false });
    const [rowData, setRowData] = useState(labComponentState.rowData);
    const [editRowData, setEditRowData] = useState(null);
    const [allCOE, setAllCOE] = useState(labComponentState.options.allCOE || []);
    const [loadingData, setLoadingData] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [labTypes, setLabTypes] = useState(labComponentState.options.labTypes || []);
    const [semesters, setSemesters] = useState(labComponentState.options.semesters || []);
    const [enrolmentYearOptions, setEnrolmentYearOptions] = useState(labComponentState.options.enrolmentYearOptions || []);
    const [isViewTriggered, setIsViewTriggered] = useState(labComponentState.isViewTriggered);
    const [user, setUser] = useState({});

    const [selectedFilters, setSelectedFilters] = useState(() => {
        if (labComponentState.selectedFilters && labComponentState.selectedFilters.selectedCOE) {
            return labComponentState.selectedFilters;
        }
        const user = JSON.parse(localStorage.getItem('user')) || {};
        return {
            selectedCOE: user.coeId || user.coe_id || '',
            labType: '',
            currentSemester: '',
            enrolmentYear: '',
        };
    });

    useEffect(() => {
        setLabComponentState({
            selectedFilters, isViewTriggered, rowData, 
            options: { allCOE, labTypes, semesters, enrolmentYearOptions },
            hasFetched: labComponentState.hasFetched
        });
    }, [selectedFilters, isViewTriggered, rowData, 
        allCOE, labTypes, semesters, enrolmentYearOptions,
        setLabComponentState, labComponentState.hasFetched]);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("user") || '{}');
        setUser(userData);

        if (allCOE.length === 0 || labTypes.length === 0) {
            const fetchInitialData = async () => {
                try {
                    setLoadingInitial(true);
                    const [coeRes, labTypeRes] = await Promise.all([getAllCOE(), getLabType()]);
                    setAllCOE(coeRes?.data?.data || []);
                    setLabTypes(labTypeRes?.data?.data?.value || []);
                } catch (error) {
                    toast.error(error?.response?.data?.message || error?.message || 'Error loading initial data');
                } finally {
                    setLoadingInitial(false);
                }
            };
            fetchInitialData();
        }
    }, []);

    useEffect(() => {
        if (!selectedFilters.selectedCOE) {
            setSemesters([]);
            setEnrolmentYearOptions([]);
            return;
        }

        if (semesters.length > 0 && enrolmentYearOptions.length > 0) {
            return; // Already cached
        }

        const fetchSemestersAndYears = async () => {
            try {
                const [semRes, yearRes] = await Promise.all([
                    getAllSemesters(selectedFilters.selectedCOE),
                    getAllEnrolmentYears(selectedFilters.selectedCOE),
                ]);
                const sortedSemesters = semRes.data.data?.sort((a, b) => a - b) || [];
                const sortedYears = yearRes.data.data?.sort((a, b) => b - a) || [];
                setSemesters(sortedSemesters);
                setEnrolmentYearOptions(sortedYears);
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || 'Error fetching semesters or enrolment years');
            }
        };

        fetchSemestersAndYears();
    }, [selectedFilters.selectedCOE]);

    const handleModalToggle = (modalName, data = null) => {
        setOpenModal(prev => ({ ...prev, [modalName]: !prev[modalName] }));
        setEditRowData(data);
    };

    const getAllLabsData = async (filtersOverride = null) => {
        const filters = filtersOverride || selectedFilters;
        try {
            setLoadingData(true);
            const response = await getAllCoeLabs(
                filters.selectedCOE,
                filters.labType,
                filters.currentSemester,
                filters.enrolmentYear
            );
            setRowData(response?.data?.data || []);
            setLabComponentState(prev => ({ ...prev, hasFetched: true }));
        } catch (error) {
            setRowData([]);
            toast.error(error?.response?.data?.message || error?.message || 'Error fetching labs');
        } finally {
            setLoadingData(false);
        }
    };

    const handleView = () => {
        if (!selectedFilters.selectedCOE) {
            toast.error("Please select a COE to view.");
            return;
        }
        setIsViewTriggered(true);
        getAllLabsData();
    };

    const handleEditClick = (data) => {
        handleModalToggle('userForm', data);
    };

    const handleFilterChange = (field) => (event) => {
        const value = event.target.value;
        if (field === 'selectedCOE') {
            setSemesters([]);
            setEnrolmentYearOptions([]);
            setIsViewTriggered(false);
            setRowData([]);
        }
        setSelectedFilters(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'selectedCOE') {
                updated.currentSemester = '';
                updated.enrolmentYear = '';
                updated.labType = '';
            }
            if (isViewTriggered && field !== 'selectedCOE') {
                getAllLabsData(updated);
            }
            return updated;
        });
    };

    return (
        <Box className="dashboard-container" >
            <Box sx={{ width: "100%", mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md>
                        <FormControl fullWidth size="small">
                            <InputLabel id="select-coe-label">Select COE*</InputLabel>
                            <Select
                                labelId="select-coe-label"
                                value={selectedFilters.selectedCOE}
                                onChange={handleFilterChange('selectedCOE')}
                                label="Select COE*"
                            >
                                {allCOE.map(coe => (
                                    <MenuItem key={coe.id} value={coe.id}>{coe.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md>
                        <FormControl fullWidth size="small">
                            <InputLabel id="select-lab-type-label">Lab Type</InputLabel>
                            <Select
                                labelId="select-lab-type-label"
                                value={selectedFilters.labType}
                                onChange={handleFilterChange('labType')}
                                label="Lab Type"
                            >
                                <MenuItem value="">None</MenuItem>
                                {labTypes.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md>
                        <FormControl fullWidth size="small">
                            <InputLabel id="select-semester-label">Current Semester</InputLabel>
                            <Select
                                labelId="select-semester-label"
                                value={selectedFilters.currentSemester}
                                onChange={handleFilterChange('currentSemester')}
                                label="Current Semester"
                            >
                                <MenuItem value="">None</MenuItem>
                                {semesters.filter(sem => sem != null).map(sem => (
                                    <MenuItem key={sem} value={sem}>{sem}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md>
                        <FormControl fullWidth size="small">
                            <InputLabel id="select-enrolment-year-label">Enrolment Year</InputLabel>
                            <Select
                                labelId="select-enrolment-year-label"
                                value={selectedFilters.enrolmentYear}
                                onChange={handleFilterChange('enrolmentYear')}
                                label="Enrolment Year"
                            >
                                <MenuItem value="">None</MenuItem>
                                {enrolmentYearOptions.map(year => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm="auto">
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                            <Button
                                onClick={handleView}
                                variant="contained"
                                color="primary"
                                disabled={loadingData || loadingInitial || !selectedFilters.selectedCOE}
                                sx={{ minWidth: "100px", height: "40px" }}
                            >
                                View
                            </Button>

                            {isViewTriggered && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleModalToggle('userForm')}
                                    sx={{ minWidth: "130px", height: "40px" }}
                                >
                                    Create Lab
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <LabPracticeModal
                open={openModal.userForm}
                onClose={() => handleModalToggle('userForm')}
                editRowData={editRowData}
                getAllLabsData={getAllLabsData}
                allCOE={allCOE}
                user={user}
            />

            {!isViewTriggered ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '350px', width: '100%', mt: 4 }}>
                    <CodeIcon sx={{ fontSize: 100, color: '#e0e0e0', mb: 2 }} />
                    <Typography sx={{ color: '#5f6368', fontSize: '18px', fontWeight: 400, textAlign: 'center' }}>
                        {!selectedFilters.selectedCOE
                            ? 'Lab details will appear here after selecting COE.'
                            : 'Click VIEW to display lab details.'}
                    </Typography>
                </Box>
            ) : loadingData && rowData.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '350px', width: '100%', mt: 4 }}>
                    <CircularProgress size={60} />
                </Box>
            ) : (
                <Box className="dashboard-box-container" sx={{ mt: 3 }}>
                    <LabPracticeTable
                        loadingData={loadingData}
                        setLoadingData={setLoadingData}
                        rowData={rowData}
                        handleEditClick={handleEditClick}
                        getAllLabsData={getAllLabsData}
                        selectedCOE={selectedFilters.selectedCOE}
                        labType={selectedFilters.labType}
                        currentSemester={selectedFilters.currentSemester}
                        enrolmentYear={selectedFilters.enrolmentYear}
                    />
                </Box>
            )}
        </Box>
    );
};

export default LabComponent;
